// Single source of truth for the data rows of a quote table. The preview,
// the PDF (screenshot of the preview) and the XLSX export all iterate this
// list, so hiding/reordering a row here keeps the three outputs in sync.
//
// Every accessor receives a ctx: { vm, product, groupIdx, idx, globalIdx }
// where vm is the BulkEditQuoteModal instance.
//
// xlsx(ctx) returns one of:
//   { value }                         plain text / number
//   { value, numFmt }                 number with Excel number format
//   { formula(ref), result, numFmt }  ref(rowKey) -> "C12" or null when the
//                                     row is hidden; returning null from
//                                     formula falls back to the static result
//   { text, hyperlink }

const isBlank = (v) => v === undefined || v === null || String(v).trim() === "";
// A manually typed total/unit price; 0 and empty mean "compute it"
const hasOverride = (v) => !isBlank(v) && Number(v) !== 0;

// Same parsing as the preview (Number()), so Excel and the preview agree:
// "27" -> 27, "5.5" -> 5.5; "1,200" / "5,5" / "27$" / "" -> null (kept as text)
export function toNumber(v) {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (isBlank(v)) return null;
  const n = Number(String(v).trim());
  return Number.isFinite(n) ? n : null;
}

// Math.ceil that ignores float noise (100 * (6 + 3.3) = 930.0000000000001),
// matching Excel's ROUNDUP which works on 15 significant digits.
export const ceilMoney = (x) => Math.ceil(Number(x.toPrecision(15)));

// Numbers stay numbers in Excel but must look exactly like the preview:
// "27.50" keeps 2 decimals via numFmt, "007" stays text.
const numberOrText = (v) => {
  const n = toNumber(v);
  if (n === null) return { value: isBlank(v) ? "" : String(v).trim() };
  const raw = String(v).trim();
  if (typeof v === "number" || String(n) === raw) return { value: n };
  const decimals = raw.match(/^-?\d+\.(\d+)$/);
  if (decimals && Number(raw) === n) {
    return { value: n, numFmt: "0." + "0".repeat(decimals[1].length) };
  }
  return { value: raw };
};

const areaNumber = ({ vm, groupIdx, idx }) =>
  Number(vm.areaInputStrings[groupIdx] && vm.areaInputStrings[groupIdx][idx]) ||
  0;

// $/m2/month rates are small numbers, so "5,5" is a vietnamese decimal comma
// (5.5), not a thousands separator. Anything else unparsable counts as 0.
export const parseRate = (v) => {
  if (typeof v === "string" && /^\s*\d+,\d{1,2}\s*$/.test(v)) {
    return Number(v.replace(",", "."));
  }
  return Number(v) || 0;
};

export const unitPrice = (product) => {
  const override = parseRate(product.rent_detail.unit_rent_and_service);
  if (override) return override;
  return (
    parseRate(product.rent_detail.basic_rent_cost) +
    parseRate(product.rent_detail.service_cost)
  );
};

export const totalUsd = (ctx) =>
  ceilMoney(areaNumber(ctx) * unitPrice(ctx.product));
export const totalVnd = (ctx) =>
  ceilMoney(areaNumber(ctx) * unitPrice(ctx.product) * ctx.vm.currency);

const formatMoney = (override, computed, suffix) =>
  (!hasOverride(override)
    ? computed.toLocaleString()
    : Number(override)
    ? Number(override).toLocaleString()
    : String(override)) +
  " " +
  suffix;

// Plain field on the product object
const productField = (getObj, field) => ({
  display: (ctx) => getObj(ctx.product)[field] || " ",
  getInput: (ctx) => getObj(ctx.product)[field],
  setInput: (ctx, v) => ctx.vm.$set(getObj(ctx.product), field, v),
  xlsx: (ctx) => numberOrText(getObj(ctx.product)[field]),
});

// Per-quote array on the modal (floor, vacant time, note)
const vmArrayField = (arrayName) => ({
  display: (ctx) => ctx.vm[arrayName][ctx.globalIdx] || " ",
  getInput: (ctx) => ctx.vm[arrayName][ctx.globalIdx],
  setInput: (ctx, v) => ctx.vm.$set(ctx.vm[arrayName], ctx.globalIdx, v),
  xlsx: (ctx) => ({ value: (ctx.vm[arrayName][ctx.globalIdx] || "").trim() }),
});

const self = (p) => p;
const detail = (p) => p.product_detail;
const rent = (p) => p.rent_detail;

export const QUOTE_ROWS = [
  {
    key: "name",
    label: "name",
    lines: 1,
    highlight: true,
    bold: true,
    ...productField(self, "name"),
  },
  {
    key: "rank",
    label: "rank",
    lines: 1,
    ...productField(self, "rank"),
  },
  {
    key: "address",
    label: "address",
    lines: 2,
    display: (ctx) => ctx.vm.getFullAddress(ctx.product.address) || " ",
    getInput: (ctx) => ctx.product.address.detail,
    setInput: (ctx, v) => ctx.vm.$set(ctx.product.address, "detail", v),
    xlsx: (ctx) => ({ value: ctx.vm.getFullAddress(ctx.product.address) }),
  },
  {
    key: "structure",
    label: "structure",
    lines: 2,
    ...productField(detail, "structure"),
  },
  {
    key: "empty_area_detail",
    label: "area",
    unit: "m2",
    lines: 1,
    highlight: true,
    display: (ctx) =>
      (ctx.vm.convertToString(ctx.product.product_detail.empty_area) || " ") +
      " m2",
    getInput: (ctx) => ctx.vm.areaInputStrings[ctx.groupIdx][ctx.idx],
    setInput: (ctx, v) =>
      ctx.vm.$set(ctx.vm.areaInputStrings[ctx.groupIdx], ctx.idx, v),
    commit: (ctx) => ctx.vm.onAreaDetailBlur(ctx.groupIdx, ctx.idx),
    xlsx: (ctx) => {
      const areas = ctx.product.product_detail.empty_area || [];
      const single = areas.length === 1 ? toNumber(areas[0]) : null;
      if (single !== null) return { value: single, numFmt: 'General" m2"' };
      if (!areas.length) return { value: "" };
      return { value: ctx.vm.convertToString(areas) + " m2" };
    },
  },
  {
    key: "floor",
    label: "floor",
    lines: 1,
    highlight: true,
    ...vmArrayField("currentFloor"),
  },
  {
    key: "basic_rent_cost",
    label: "basicRent",
    unit: "usdPerM2Month",
    lines: 1,
    ...productField(rent, "basic_rent_cost"),
  },
  {
    key: "service_cost",
    label: "serviceCost",
    unit: "usdPerM2Month",
    lines: 1,
    ...productField(rent, "service_cost"),
  },
  {
    key: "unit_rent_and_service",
    label: "unitRentAndService",
    unit: "usd",
    lines: 1,
    highlight: true,
    display: (ctx) => unitPrice(ctx.product) || " ",
    getInput: (ctx) => ctx.product.rent_detail.unit_rent_and_service,
    setInput: (ctx, v) =>
      ctx.vm.$set(ctx.product.rent_detail, "unit_rent_and_service", v),
    xlsx: (ctx) => ({
      result: unitPrice(ctx.product),
      formula: (ref) => {
        const rd = ctx.product.rent_detail;
        if (toNumber(rd.unit_rent_and_service)) return null;
        const basic = ref("basic_rent_cost");
        const service = ref("service_cost");
        if (!basic || !service) return null;
        if (toNumber(rd.basic_rent_cost) === null) return null;
        if (toNumber(rd.service_cost) === null) return null;
        return `${basic}+${service}`;
      },
    }),
  },
  {
    key: "total_usd",
    label: "totalUsd",
    unit: "usd",
    lines: 1,
    highlight: true,
    display: (ctx) =>
      formatMoney(
        ctx.product.rent_detail.total_usd,
        totalUsd(ctx),
        ctx.vm.t.usd
      ),
    getInput: (ctx) => ctx.product.rent_detail.total_usd,
    setInput: (ctx, v) => ctx.vm.$set(ctx.product.rent_detail, "total_usd", v),
    xlsx: (ctx) => {
      const override = ctx.product.rent_detail.total_usd;
      const numFmt = `#,##0" ${ctx.vm.t.usd}"`;
      if (hasOverride(override)) return { ...numberOrText(override), numFmt };
      return {
        result: totalUsd(ctx),
        numFmt,
        formula: (ref) => {
          const area = ref("empty_area_detail");
          const unit = ref("unit_rent_and_service");
          if (!area || !unit || !areaNumber(ctx)) return null;
          return `ROUNDUP(${area}*${unit},0)`;
        },
      };
    },
  },
  {
    key: "total_vnd",
    label: "totalVnd",
    unit: "vnd",
    lines: 1,
    highlight: true,
    display: (ctx) =>
      formatMoney(
        ctx.product.rent_detail.total_vnd,
        totalVnd(ctx),
        ctx.vm.t.vnd
      ),
    getInput: (ctx) => ctx.product.rent_detail.total_vnd,
    setInput: (ctx, v) => ctx.vm.$set(ctx.product.rent_detail, "total_vnd", v),
    xlsx: (ctx) => {
      const override = ctx.product.rent_detail.total_vnd;
      const numFmt = `#,##0" ${ctx.vm.t.vnd}"`;
      if (hasOverride(override)) return { ...numberOrText(override), numFmt };
      return {
        result: totalVnd(ctx),
        numFmt,
        formula: (ref) => {
          const area = ref("empty_area_detail");
          const unit = ref("unit_rent_and_service");
          if (!area || !unit || !areaNumber(ctx)) return null;
          return `ROUNDUP(${area}*${unit}*${ref.rate},0)`;
        },
      };
    },
  },
  {
    key: "vacant_time",
    label: "vacantTime",
    lines: 1,
    ...vmArrayField("availableTime"),
  },
  {
    key: "over_time_cost",
    label: "overTimeCost",
    lines: 1,
    ...productField(rent, "over_time_cost"),
  },
  {
    key: "rent_deposit",
    label: "deposit",
    lines: 1,
    ...productField(rent, "rent_deposit"),
  },
  {
    key: "payment_method",
    label: "payment",
    lines: 1,
    ...productField(rent, "rent_area"),
  },
  {
    key: "motor_park_cost",
    label: "motorPark",
    unit: "perVehicleMonth",
    lines: 1,
    ...productField(rent, "motor_park_cost"),
  },
  {
    key: "car_park_cost",
    label: "carPark",
    unit: "perVehicleMonth",
    lines: 1,
    ...productField(rent, "car_park_cost"),
  },
  {
    key: "decor_time",
    label: "fitOut",
    lines: 1,
    ...productField(rent, "decor_time"),
  },
  {
    key: "website",
    label: "website",
    lines: 2,
    cellClass: "website-link-cell",
    spanClass: "text-truncate-table",
    ...productField(self, "website"),
    xlsx: (ctx) => {
      const text = (ctx.product.website || "").trim();
      if (!text) return { value: "" };
      const hyperlink = /^https?:\/\//i.test(text) ? text : "https://" + text;
      return { text, hyperlink };
    },
  },
  {
    key: "note",
    label: "note",
    lines: 1,
    highlight: true,
    ...vmArrayField("notedArray"),
  },
];
